import { useMemo } from "react";
import { Avatar } from "@/components/atoms/Avatar";
import { Badge } from "@/components/atoms/Badge";
import { CopyButton } from "@/components/atoms/CopyButton";
import { ColumnFilter } from "@/components/molecules/ColumnFilter";
import { MenuSelect } from "@/components/molecules/MenuSelect";
import { SyncStatus } from "@/components/molecules/SyncStatus";
import { DataTable, type Column, type RowSelection } from "@/components/organisms/DataTable";
import { formatRelativeTime } from "@/lib/format";
import type { SortDirection } from "@/types/api";
import type { ContactFilter, ContactSummary } from "@/types/entities";

interface Props {
  rows: ContactSummary[];
  loading: boolean;
  selectedKey: string | null;
  onSelect: (row: ContactSummary,index: number) => void;
  sort?: { field: string; direction: SortDirection };
  onSortChange: (field: string, direction: SortDirection) => void;
  /** Refetches the list after a row's resync is queued, so the status updates. */
  onResynced?: () => void;
  /** Supply to show the checkbox column for bulk delete. */
  selection?: RowSelection<ContactSummary>;
  /** Current filter, so column headers can show what they are narrowing by. */
  filter: ContactFilter;
  onFilterChange: (next: Partial<ContactFilter>) => void;
}

function contactDisplayName(contact: ContactSummary): string {
  return contact.name || contact.contactPerson || "(unnamed)";
}

function contactTypePillClass(category: string | null | undefined): string {
  const value = category?.trim().toUpperCase();
  const compactValue = value?.replace(/\s+/g, "");
  if (value === "UNKNOWN") return "bg-[#009BF5]/10 text-[#009BF5] border-[#009BF5]/30";
  if (value === "CLIENT") return "bg-cyan-50 text-cyan-700 border-cyan-200";
  if (compactValue === "CONTACTRELATIONSHIP") return "bg-violet-50 text-violet-700 border-violet-200";
  return "bg-[#009BF5]/10 text-[#009BF5] border-[#009BF5]/30";
}

function contactLabelNames(contact: ContactSummary): string[] {
  return (contact.labels ?? [])
    .map((label) => label?.name?.trim() ?? "")
    .filter((name) => name.length > 0);
}

const SYNC_STATUS_OPTIONS = [
  { value: "", label: "Any sync status" },
  { value: "COMPLETED", label: "In Sync" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "FAILED", label: "Failed" },
  { value: "CHECKPOINT", label: "Checkpoint" },
];

const buildColumns = (
  onResynced: (() => void) | undefined,
  filter: ContactFilter,
  onFilterChange: (next: Partial<ContactFilter>) => void,
): Column<ContactSummary>[] => [
  {
    id: "name",
    header: "Contact",
    sortable: true,
    width: "w-[280px]",
    cell: (r) => (
      <div className="flex min-w-0 items-center gap-2">
        <Avatar
          name={contactDisplayName(r)}
          size="sm"
          colorful
          className="shrink-0"
        />
        <div className="min-w-0 overflow-hidden leading-tight">
          <div className="truncate text-[13px] font-semibold text-slate-800" title={contactDisplayName(r)}>{contactDisplayName(r)}</div>
          <div className="group/copy flex min-w-0 items-center gap-1">
            <div className="truncate text-[11px] text-bridge-muted" title={r.email ?? undefined}>{r.email ?? "—"}</div>
            <CopyButton value={r.email} label="Email" />
          </div>
        </div>
        {r.crmSyncStatus === "IN_PROGRESS" && (
           <span className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
             <span className="relative flex h-1.5 w-1.5">
               <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
               <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-500"></span>
             </span>
             Syncing
           </span>
        )}
      </div>
    ),
  },
  {
    id: "mobilePhone",
    header: "Phone",
    sortable: false,
    width: "w-[180px]",
    cell: (r) =>
      r.mobilePhone ? (
        <div className="group/copy flex min-w-0 items-center gap-1">
          <span className="truncate text-xs font-medium text-slate-700">{r.mobilePhone}</span>
          <CopyButton value={String(r.mobilePhone)} label="Phone number" />
        </div>
      ) : (
        <span className="text-xs text-bridge-subtle">—</span>
      ),
  },
  {
    id: "category",
    header: "Type",
    sortable: false,
    width: "w-[150px]",
    cell: (r) =>
      r.category ? (
        <Badge tone="neutral" className={`h-5 whitespace-nowrap rounded-full px-2 text-[10px] ${contactTypePillClass(r.category)}`}>
          {r.category}
        </Badge>
      ) : (
        <span className="text-bridge-subtle">—</span>
      ),
  },
  {
    id: "labels",
    header: "Label",
    sortable: false,
    width: "w-[260px]",
    align: "center",
    // The full list goes in the tooltip — the cell only has room for the first two.
    cellTooltip: (r) => contactLabelNames(r).join(", "),
    cell: (r) => {
      const names = contactLabelNames(r);
      if (names.length === 0) return <span className="text-bridge-subtle">—</span>;
      const extra = names.length - 2;
      return (
        <div className="flex min-w-0 flex-wrap items-center justify-center gap-1">
          {names.slice(0, 2).map((name, i) => (
            <Badge key={`${name}-${i}`} tone="neutral" className="h-5 max-w-[160px] truncate whitespace-nowrap rounded-full px-2 text-[10px]">
              {name}
            </Badge>
          ))}
          {extra > 0 && (
            <span className="text-xs font-medium text-bridge-muted">+{extra}</span>
          )}
        </div>
      );
    },
  },
  {
    id: "town",
    header: "Location",
    sortable: true,
    width: "w-[200px]",
    cell: (r) => (
      r.town || r.postcode ? (
        <div className="text-xs font-medium leading-tight text-slate-700">
          {r.town ?? "—"}
          <div className="text-[11px] font-normal text-bridge-muted">{r.postcode ?? ""}</div>
        </div>
      ) : (
        <span className="text-xs font-medium text-bridge-muted">+ Add location</span>
      )
    ),
  },
  {
    id: "syncStatus",
    header: "Sync",
    sortable: false,
    width: "w-[200px]",
    headerAccessory: (
      <ColumnFilter
        label="Sync"
        tooltip="Show contacts by their CRM sync state."
        active={Boolean(filter.crmSyncStatus)}
        onClear={() => onFilterChange({ crmSyncStatus: undefined })}
      >
        <MenuSelect
          hideLabel
          value={filter.crmSyncStatus ?? ""}
          onChange={(v) => onFilterChange({ crmSyncStatus: v || undefined })}
          options={SYNC_STATUS_OPTIONS}
          placeholder="Any sync status"
        />
      </ColumnFilter>
    ),
    cell: (r) => (
      <SyncStatus
        crmSyncStatus={r.crmSyncStatus || r.syncAlto}
        entityType="CONTACT"
        entityId={r.id}
        align="start"
        compact
        onResynced={onResynced}
      />
    ),
  },
  {
    id: "atlasModifiedAt",
    header: "Updated",
    sortable: true,
    width: "w-[130px]",
    cell: (r) => <span className="text-xs font-medium text-bridge-muted">{formatRelativeTime(r.atlasModifiedAt)}</span>,
  },
];

export function ContactsTable({
  rows,
  loading,
  selectedKey,
  onSelect,
  sort,
  onSortChange,
  onResynced,
  selection,
  filter,
  onFilterChange,
}: Props) {
  const columns = useMemo(
    () => buildColumns(onResynced, filter, onFilterChange),
    [onResynced, filter, onFilterChange],
  );

  return (
    <DataTable
      rows={rows}
      columns={columns}
      loading={loading}
      dense
      rowKey={(r) => r.id}
      onRowClick={onSelect}
      selectedKey={selectedKey}
      markVisitedRow
      sort={sort}
      onSortChange={onSortChange}
      selection={selection}
      customization={{
        tableId: "contacts",
        lockedColumnIds: ["name"],
        pinnedStartColumnIds: ["name"],
      }}
      emptyTitle="No contacts match your filters"
      emptyDescription="Try widening your search or clearing the category filter."
    />
  );
}
